import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import KeyboardDoubleArrowUpRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowUpRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";

import "./Commercial.css";
import openWhatsApp from "../utils/sendWhatsapp";
import com1 from "../assets/commer1.png";
import brandOut from "../assets/brand-outlet.png";
import retailWalkWay from "../assets/retail-walkway.png";
import businessInterior from "../assets/business-interios.png";
import commercialFeel from "../assets/commercial-feel.png";
const galleryImages = [
  {
    title: "Ground Floor Retail",
    image: com1,
  },
  {
    title: "Brand Outlet",
    image: brandOut,
  },
  {
    title: "Modern Studio Setup",
    image:
      "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1800&q=75",
  },
  {
    title: "Retail Walkway",
    image: retailWalkWay,
  },
  {
    title: "Business Interior",
    image: businessInterior,
  },
  {
    title: "Premium Commercial Feel",
    image: commercialFeel,
  },
];

const floors = [
  {
    tag: "BASEMENT",
    icon: <LayersRoundedIcon />,
    title: "Basement Commercial",
    desc: "Utility-first spaces for operations, services, and storage-based business models.",
    bullets: ["More usable area", "Great utility value", "Service-friendly"],
  },
  {
    tag: "GROUND FLOOR",
    icon: <StorefrontRoundedIcon />,
    title: "Ground Floor Shops",
    desc: "Maximum visibility and walk-ins — the best choice for retail and customer-heavy brands.",
    bullets: [
      "Highest exposure",
      "Fast customer discovery",
      "Strong retail demand",
    ],
  },
  {
    tag: "MEZZANINE",
    icon: <KeyboardDoubleArrowUpRoundedIcon />,
    title: "Mezzanine Spaces",
    desc: "Premium vibe for services like salons, clinics, studios, and offices — privacy + class.",
    bullets: ["Premium client feel", "Better privacy", "High rental demand"],
  },
];

const Commercial = () => {
  const { ref, inView } = useInView({ threshold: 0.12, triggerOnce: true });

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const active = useMemo(() => galleryImages[activeIndex], [activeIndex]);

  const onImageClick = (idx) => {
    setActiveIndex(idx);
    setOpen(true);
  };

  const onBook = () => {
    // alert("Commercial booking will open here (we'll connect it next).");
    openWhatsApp();
  };

  return (
    <section className="cPro2" id="commercial" ref={ref}>
      <div className="cPro2__container">
        {/* HEADER */}
        <motion.div
          className="cPro2__head"
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.75 }}
        >
          <div className="cPro2__kicker">HB HEIGHT • COMMERCIAL SPACES</div>

          <h2 className="cPro2__title">
            Start your business here — or rent it out for monthly income.
          </h2>

          <p className="cPro2__sub">
            Commercial units at HB Height are designed to create real value. A
            premium location gives your brand trust and footfall. If you don’t
            run a business, rent it out and build steady cashflow.
          </p>

          <div className="cPro2__highlights">
            <div className="cPro2__hl">
              <span className="cPro2__hlIcon">
                <VisibilityRoundedIcon />
              </span>
              <div>
                <div className="cPro2__hlTitle">Premium visibility</div>
                <div className="cPro2__hlText">
                  Customers trust premium addresses faster.
                </div>
              </div>
            </div>

            <div className="cPro2__hl">
              <span className="cPro2__hlIcon">
                <PaidRoundedIcon />
              </span>
              <div>
                <div className="cPro2__hlTitle">Rental potential</div>
                <div className="cPro2__hlText">
                  Own an asset that can pay you monthly.
                </div>
              </div>
            </div>

            <div className="cPro2__hl">
              <span className="cPro2__hlIcon">
                <StorefrontRoundedIcon />
              </span>
              <div>
                <div className="cPro2__hlTitle">Business growth</div>
                <div className="cPro2__hlText">
                  Launch a brand, scale faster, expand smarter.
                </div>
              </div>
            </div>
          </div>

          <div className="cPro2__ctaRow">
            <button className="cPro2__btn" onClick={onBook}>
              Book Commercial Space <ArrowOutwardRoundedIcon />
            </button>
            <div className="cPro2__note">
              Limited units • best-facing shops go first
            </div>
          </div>
        </motion.div>

        {/* IMAGE GRID */}
        {/* GALLERY SLIDER */}
        <motion.div
          className="cPro2__gallery"
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.75, delay: 0.06 }}
        >
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={{ clickable: true }}
            autoplay={{ delay: 2800, disableOnInteraction: false }}
            spaceBetween={14}
            slidesPerView={3}
            breakpoints={{
              0: { slidesPerView: 1.05 },
              520: { slidesPerView: 1.4 },
              820: { slidesPerView: 2.2 },
              1100: { slidesPerView: 3 },
            }}
            className="cPro2__swiper"
          >
            {galleryImages.map((u, idx) => (
              <SwiperSlide key={idx}>
                <button
                  type="button"
                  className="cPro2__gCard"
                  onClick={() => onImageClick(idx)}
                  aria-label={`Open image: ${u.title}`}
                >
                  <div className="cPro2__gImgWrap">
                    <img
                      src={u.image}
                      alt={u.title}
                      className="cPro2__gImg"
                      loading="lazy"
                    />
                  </div>

                  <div className="cPro2__gTitle">{u.title}</div>
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>

        {/* FLOOR CARDS */}
        <motion.div
          className="cPro2__floors"
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.75, delay: 0.12 }}
        >
          {floors.map((f, idx) => (
            <div className="cPro2__card" key={idx}>
              <div className="cPro2__cardTop">
                <span className="cPro2__tag">{f.tag}</span>
                <span className="cPro2__cardIcon">{f.icon}</span>
              </div>

              <div className="cPro2__cardTitle">{f.title}</div>
              <div className="cPro2__cardDesc">{f.desc}</div>

              <div className="cPro2__bullets">
                {f.bullets.map((b, i) => (
                  <div className="cPro2__bullet" key={i}>
                    <span className="cPro2__dot" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </motion.div>

        {/* VIEWER */}
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <div className="cPro2__viewer">
            <div className="cPro2__viewerTop">
              <div className="cPro2__viewerTitle">{active?.title}</div>
              <IconButton onClick={() => setOpen(false)}>
                <CloseRoundedIcon />
              </IconButton>
            </div>

            <div className="cPro2__viewerBody">
              <img
                className="cPro2__viewerImg"
                src={active?.image}
                alt={active?.title}
              />
            </div>
          </div>
        </Dialog>
      </div>
    </section>
  );
};

export default Commercial;
